import React, { useContext, useState, useMemo, useEffect } from 'react';
import { MachineContext } from '../DataContexts/MachineContext';
import { PlaylistDataContext } from '../DataContexts/PlaylistDataContext';
import { PlaytreeDataContext, normalizeSublists } from '../DataContexts/PlaytreeDataContext';
import { DataContext } from '../DataContexts/DataContext';
import EventLogsBlock from './EventLogsBlock';
import {
  OpenDaysDots, toOpenDaysDraft, fromOpenDaysDraft, toggleOpenDay, getBrusselsTodayIndex,
  openDaysEqual,
} from './openDays';
import './MachineDetails.css';

// DJB-130: no longer a standalone page with its own machine list — the detail
// containers (Info, Current Stats, Financial History, Playlists, Optional
// Features, Downloads, Token Config — the DJB-137 order — plus the DJB-187
// Event Logs block) render inline below the Accounts Overview table for the
// account passed via the `machine` prop.
const MachineDetails = ({ machine }) => {
  const { machines, toggleMachinePlaylist, updatePlaylistPriority, fetchCreditRequests, optionalFeatures, updateOptionalFeatures, updateDeactivateType, bonusConfiguration, updateBonusConfiguration, longTrackPriceModes, updateLongTrackPriceMode, updateMachine, updateMachineWorkdays, fetchTokenBalance, resetTokens, addTokens, fetchTokenConfigs, updateTokenConfig, clearBillingPeriod } = useContext(MachineContext);
  const { playlists } = useContext(PlaylistDataContext);
  const { playtrees } = useContext(PlaytreeDataContext);
  const { artists } = useContext(DataContext);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [playlistSearchTerm, setPlaylistSearchTerm] = useState('');
  // DJB-157: the two playlist tables are mutually exclusive. DJB-170: both
  // tables are back to fixed heights that scroll internally — only the
  // open/close exclusivity remains. DJB-250: both start collapsed.
  const [openPlaylistTable, setOpenPlaylistTable] = useState(null);
  const globalsTableOpen = openPlaylistTable === 'globals';
  const localsTableOpen = openPlaylistTable === 'locals';
  const togglePlaylistTable = (table) =>
    setOpenPlaylistTable(open => (open === table ? null : table));
  const [subscribingCategoryId, setSubscribingCategoryId] = useState(null);
  // DJB-135: optional-feature edits collect in a draft and only persist on Save
  const [featureDraft, setFeatureDraft] = useState({});
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [historyDays, setHistoryDays] = useState(30);
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState(null);
  const [savingInfo, setSavingInfo] = useState(false);
  const [tokenBalance, setTokenBalance] = useState(null);
  const [tokenConfigs, setTokenConfigs] = useState(null);
  const [tokenConfigsLoading, setTokenConfigsLoading] = useState(false);
  const [tokenConfigsError, setTokenConfigsError] = useState(null);
  const [editingTokenConfigs, setEditingTokenConfigs] = useState({});
  const [showClearPeriodDialog, setShowClearPeriodDialog] = useState(false);
  const [clearingPeriod, setClearingPeriod] = useState(false);

  // Distinct machine types across all machines, for the type dropdown
  const machineTypes = useMemo(
    () => [...new Set(machines.map(m => m.type).filter(Boolean))].sort(),
    [machines]
  );

  // Distinct provinces across all machines, for the province dropdown
  const provinces = useMemo(
    () => [...new Set(machines.map(m => m.province).filter(Boolean))].sort(),
    [machines]
  );

  const handleStartEditInfo = () => {
    setInfoForm({
      name: selectedMachine.name ?? '',
      street: selectedMachine.street ?? '',
      housenumber: selectedMachine.housenumber ?? '',
      postalcode: selectedMachine.postalcode ?? '',
      city: selectedMachine.city ?? '',
      province: selectedMachine.province ?? '',
      type: selectedMachine.type ?? '',
      overeenkomst: selectedMachine.overeenkomst ?? 'UNASSIGNED',
      openDays: toOpenDaysDraft(selectedMachine.openDays),
    });
    setIsEditingInfo(true);
  };

  const handleCancelEditInfo = () => {
    setIsEditingInfo(false);
    setInfoForm(null);
  };

  const handleInfoFieldChange = (field, value) => {
    setInfoForm(prev => ({ ...prev, [field]: value }));
  };

  const handleToggleOpenDay = (index) => {
    setInfoForm(prev => ({
      ...prev,
      openDays: toggleOpenDay(prev.openDays, index),
    }));
  };

  // DJB-255: back to "Dagen niet gekend" — all grey, saved as null
  const handleResetOpenDays = () => {
    setInfoForm(prev => ({ ...prev, openDays: null }));
  };

  // DJB-252 / DJB-Server PR #80: the generic machine PUT no longer carries
  // openDays. The info fields go through updateMachine as before; the days
  // go through updateMachineWorkdays (PUT 7 bools, or DELETE for a reset to
  // "Dagen niet gekend") — and only when the draft actually differs from the
  // stored days, so an untouched field costs no extra request.
  const handleSaveInfo = async () => {
    if (!selectedMachine || !infoForm) return;
    setSavingInfo(true);
    try {
      const { openDays: openDaysDraft, ...infoFields } = infoForm;
      const normalizedForm = {
        ...infoFields,
        housenumber: infoFields.housenumber === '' ? null : Number(infoFields.housenumber),
        postalcode: infoFields.postalcode === '' ? null : Number(infoFields.postalcode),
      };
      const updatedMachine = { ...selectedMachine, ...normalizedForm };
      await updateMachine(selectedMachine.id, updatedMachine);

      let savedMachine = updatedMachine;
      const nextOpenDays = fromOpenDaysDraft(openDaysDraft);
      if (!openDaysEqual(nextOpenDays, selectedMachine.openDays)) {
        try {
          const workdays = await updateMachineWorkdays(selectedMachine.id, nextOpenDays);
          savedMachine = {
            ...updatedMachine,
            openDays: workdays ? workdays.openDays : nextOpenDays,
            todayStatus: workdays
              ? workdays.todayStatus
              : nextOpenDays ? updatedMachine.todayStatus : 'unknown',
          };
        } catch (err) {
          // the info fields are saved; keep the day edits on screen for a retry
          console.error('Failed to update open days:', err);
          setSelectedMachine(updatedMachine);
          return;
        }
      }

      setSelectedMachine(savedMachine);
      setIsEditingInfo(false);
      setInfoForm(null);
    } catch (err) {
      // updateMachine already surfaces an error notification
      console.error('Failed to update machine:', err);
    } finally {
      setSavingInfo(false);
    }
  };

  // Sync with the row selection in the table above; switching rows also drops
  // any in-progress info edit so the form never carries stale values over
  useEffect(() => {
    setSelectedMachine(machine ?? null);
    setIsEditingInfo(false);
    setInfoForm(null);
    setFeatureDraft({});
  }, [machine]);

  const PAID_OVEREENKOMST_TYPES = ['BASIC', 'PRO', 'UNLIMITED'];

  const hasPaidOvereenkomst = selectedMachine &&
    PAID_OVEREENKOMST_TYPES.includes(selectedMachine.overeenkomst);

  useEffect(() => {
    if (selectedMachine && PAID_OVEREENKOMST_TYPES.includes(selectedMachine.overeenkomst) && fetchTokenBalance) {
      fetchTokenBalance(selectedMachine.id).then(balance => {
        setTokenBalance(balance ?? null);
      });
    } else {
      setTokenBalance(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMachine?.id, selectedMachine?.overeenkomst]);

  const loadTokenConfigs = async () => {
    setTokenConfigsLoading(true);
    setTokenConfigsError(null);
    try {
      const configs = await fetchTokenConfigs();
      if (configs) {
        setTokenConfigs(configs);
      } else {
        setTokenConfigsError('Could not load token configs. The backend endpoint may not be available yet.');
      }
    } catch {
      setTokenConfigsError('Could not load token configs. The backend endpoint may not be available yet.');
    }
    setTokenConfigsLoading(false);
  };

  const getDaysUntilNextMonth = () => {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const diffTime = nextMonth - now;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getDefaultTokensForOvereenkomst = () => {
    if (!tokenConfigs || !selectedMachine?.overeenkomst) return null;
    const config = Array.isArray(tokenConfigs)
      ? tokenConfigs.find(c => c.overeenkomstType === selectedMachine.overeenkomst)
      : tokenConfigs[selectedMachine.overeenkomst];
    if (!config) return null;
    return typeof config === 'object' ? config.defaultTokens : config;
  };

  const handleResetTokens = async () => {
    if (!selectedMachine) return;
    try {
      const balance = await resetTokens(selectedMachine.id);
      if (balance != null) {
        setTokenBalance(balance);
      } else {
        const fetched = await fetchTokenBalance(selectedMachine.id);
        setTokenBalance(fetched ?? null);
      }
    } catch (err) {
      console.error('Failed to reset tokens:', err);
    }
  };

  const handleAddTokens = async () => {
    if (!selectedMachine || tokenBalance == null) return;
    try {
      const newBalance = tokenBalance + 10;
      const balance = await addTokens(selectedMachine.id, newBalance);
      setTokenBalance(balance != null ? balance : newBalance);
    } catch (err) {
      console.error('Failed to add tokens:', err);
    }
  };

  const handleConfirmClearPeriod = async () => {
    if (!selectedMachine) return;
    setClearingPeriod(true);
    try {
      const clearedFields = await clearBillingPeriod(selectedMachine.id);
      if (clearedFields) {
        setSelectedMachine(prev => ({ ...prev, ...clearedFields }));
      }
      setShowClearPeriodDialog(false);
    } catch (err) {
      // clearBillingPeriod already surfaces an error notification
      console.error('Failed to clear billing period:', err);
    } finally {
      setClearingPeriod(false);
    }
  };

  const handleSaveTokenConfig = async (overeenkomstType) => {
    const newAmount = editingTokenConfigs[overeenkomstType];
    if (newAmount === undefined || newAmount === '') return;
    try {
      await updateTokenConfig(overeenkomstType, parseInt(newAmount));
      await loadTokenConfigs();
      setEditingTokenConfigs(prev => {
        const next = { ...prev };
        delete next[overeenkomstType];
        return next;
      });
    } catch (err) {
      console.error('Failed to update token config:', err);
    }
  };

  const calculateRevenue = (machine) => {
    if (machine.currentCoinDeposit === undefined || machine.currentIncomeFromPayconiq === undefined) return 'N/A';
    return ((machine.currentCoinDeposit + machine.currentIncomeFromPayconiq) / 100).toFixed(2).replace('.', ',') + ' €';
  };

  const calculateDaysSinceClear = (coinsLastEmptied) => {
    if (!coinsLastEmptied || coinsLastEmptied.length < 3) return 'N/A';
    const lastClearDate = new Date(coinsLastEmptied[0], coinsLastEmptied[1] - 1, coinsLastEmptied[2]);
    const today = new Date();
    const diffTime = Math.abs(today - lastClearDate);
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  const formatLastSeen = (lastSeen) => {
    if (!lastSeen || lastSeen.length < 6) return 'Never';
    const lastSeenDate = new Date(
      lastSeen[0], lastSeen[1] - 1, lastSeen[2], lastSeen[3], lastSeen[4], lastSeen[5]
    );
    return lastSeenDate.toLocaleString('nl-BE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateAverageIncome = (machine) => {
    const days = calculateDaysSinceClear(machine.coinsLastEmptied);
    const totalIncome = (machine.currentCoinDeposit || 0) + (machine.currentIncomeFromPayconiq || 0);
    
    if (days === 'N/A' || days === 0) return 'N/A';
    
    return ((totalIncome / 100) / days).toFixed(2).replace('.', ',') + ' €';
  };

  const getGoogleMapsUrl = (machine) => {
    if (!machine) return '#';
    const address = `${machine.street} ${machine.housenumber}, ${machine.postalcode} ${machine.city}, ${machine.province}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  const handlePlaylistToggle = async (playlistId) => {
    if (!selectedMachine) return;
    
    const isSubscribing = !selectedMachine.playlists.includes(playlistId);
    
    try {
      await toggleMachinePlaylist(selectedMachine.id, playlistId, isSubscribing);
      setSelectedMachine(prev => {
        // DJB-207: mirror MachineContext — a fresh subscription starts
        // unprioritized, an unsubscribe drops the priority entry
        const playlistPriorities = { ...(prev.playlistPriorities ?? {}) };
        if (isSubscribing) {
          playlistPriorities[playlistId] = false;
        } else {
          delete playlistPriorities[playlistId];
        }
        return {
          ...prev,
          playlists: isSubscribing
            ? [...prev.playlists, playlistId]
            : prev.playlists.filter(id => id !== playlistId),
          playlistPriorities,
        };
      });
    } catch (error) {
      console.error('Failed to toggle playlist:', error);
    }
  };

  // DJB-207: star toggle marks a subscribed playlist as priority for this
  // account — same per-machine /v2 priority endpoint as the Playlist Editor
  // (DJB-62). The context call already surfaces success/error toasts.
  const isPlaylistPrioritized = (playlistId) =>
    selectedMachine?.playlistPriorities?.[playlistId] === true;

  const handlePriorityToggle = async (playlistId) => {
    if (!selectedMachine || !selectedMachine.playlists.includes(playlistId)) return;

    const prioritized = !isPlaylistPrioritized(playlistId);

    try {
      await updatePlaylistPriority(selectedMachine.id, playlistId, prioritized);
      setSelectedMachine(prev => ({
        ...prev,
        playlistPriorities: { ...(prev.playlistPriorities ?? {}), [playlistId]: prioritized },
      }));
    } catch (error) {
      console.error('Failed to toggle playlist priority:', error);
    }
  };

  // Only subscribed rows get a star; unsubscribed ones keep an empty cell so
  // the columns stay aligned.
  const renderPriorityStar = (playlist) => {
    if (!selectedMachine?.playlists?.includes(playlist.id)) return null;
    const prioritized = isPlaylistPrioritized(playlist.id);
    const label = prioritized
      ? 'Remove playlist priority for this account'
      : 'Prioritize playlist for this account';
    return (
      <button
        type="button"
        className={`playlist-star-btn${prioritized ? ' is-on' : ''}`}
        title={label}
        aria-label={label}
        aria-pressed={prioritized}
        onClick={(e) => {
          e.stopPropagation();
          handlePriorityToggle(playlist.id);
        }}
      >
        {prioritized ? '★' : '☆'}
      </button>
    );
  };

  const getPlaylistDisplayName = (playlist) =>
    playlist.name === 'YOUR FAVOURITES' ? (playlist.searchString || playlist.name) : playlist.name;

  const customGlobalPlaylists = useMemo(
    () => playlists.filter(p => p.playlistType === 'CUSTOM_GLOBAL'),
    [playlists]
  );

  const customLocalPlaylists = useMemo(
    () => playlists.filter(p => p.playlistType.startsWith('CUSTOM_') && p.playlistType !== 'CUSTOM_GLOBAL'),
    [playlists]
  );

  // DJB-133: Custom Globals grouped per category (playtree); globals that sit
  // in no category collect under Uncategorized so nothing disappears
  const globalPlaylistCategories = useMemo(() => {
    const byId = new Map(customGlobalPlaylists.map(p => [p.id, p]));
    const categorized = new Set();
    const groups = playtrees
      .map(playtree => {
        const items = normalizeSublists(playtree.sublists)
          .map(id => byId.get(id))
          .filter(Boolean);
        items.forEach(p => categorized.add(p.id));
        return { id: playtree.id, name: playtree.name, playlists: items };
      })
      .filter(group => group.playlists.length > 0);
    const uncategorized = customGlobalPlaylists.filter(p => !categorized.has(p.id));
    if (uncategorized.length > 0) {
      groups.push({ id: 'uncategorized', name: 'Uncategorized', playlists: uncategorized });
    }
    return groups;
  }, [playtrees, customGlobalPlaylists]);

  const matchesPlaylistSearch = (playlist) =>
    getPlaylistDisplayName(playlist).toLowerCase().includes(playlistSearchTerm.toLowerCase());

  // DJB-138/157: typing a search term makes sure a table is open so matches
  // stay visible; the tables are mutually exclusive now, so when both are
  // collapsed the default (Custom Globals) opens and matches in the other
  // table stay reachable through its chevron
  const handlePlaylistSearchChange = (e) => {
    const value = e.target.value;
    setPlaylistSearchTerm(value);
    if (value.trim() !== '') {
      setOpenPlaylistTable(open => open ?? 'globals');
    }
  };

  // Subscribe the machine to every playlist in the category. There is no bulk
  // backend endpoint (yet): the per-playlist /v2 subscription call runs once
  // per playlist, sequentially, so a mid-loop failure keeps earlier successes.
  const handleSubscribeCategory = async (category) => {
    if (!selectedMachine) return;
    const toSubscribe = category.playlists
      .map(p => p.id)
      .filter(id => !selectedMachine.playlists.includes(id));
    if (toSubscribe.length === 0) return;
    setSubscribingCategoryId(category.id);
    try {
      for (const playlistId of toSubscribe) {
        await toggleMachinePlaylist(selectedMachine.id, playlistId, true);
        setSelectedMachine(prev => ({
          ...prev,
          playlists: [...prev.playlists, playlistId],
        }));
      }
    } catch (error) {
      console.error('Failed to subscribe category:', error);
    } finally {
      setSubscribingCategoryId(null);
    }
  };

  const handleDaysChange = (e) => {
    const value = e.target.value;
    if (value === '' || (/^\d+$/.test(value) && parseInt(value) > 0)) {
      setHistoryDays(value);
    }
  };

  // Get optional features for the selected machine
  const selectedMachineOptionalFeatures = useMemo(() => {
    if (!selectedMachine || !optionalFeatures) return null;
    return optionalFeatures.find(feature => feature.machineId === selectedMachine.id);
  }, [selectedMachine, optionalFeatures]);

  const OVEREENKOMST_LABELS = {
    UNASSIGNED: 'Unassigned',
    BASIC: 'Basic',
    PRO: 'Pro',
    UNLIMITED: 'Unlimited',
  };

  const DEACTIVATE_TYPE_LABELS = {
    NONE: 'None',
    SUSPEND: 'Suspend',
    ARCHIVE: 'Archive',
  };

  const ACCOUNT_STATE_LABELS = {
    PENDING: 'pending',
    ACTIVE: 'active',
    DORMANT: 'dormant',
    DEAD: 'dead',
    SUSPENDED: 'suspended',
    ARCHIVED: 'archived',
  };

  const isDeactivateFlagActive = selectedMachine &&
    selectedMachine.deactivateType &&
    selectedMachine.deactivateType !== 'NONE';

  const handleDeactivateTypeChange = async (e) => {
    if (!selectedMachine) return;
    const newValue = e.target.value;
    const previousValue = selectedMachine.deactivateType;
    setSelectedMachine(prev => ({ ...prev, deactivateType: newValue }));
    try {
      const updatedMachine = await updateDeactivateType(selectedMachine.id, newValue);
      if (updatedMachine) {
        setSelectedMachine(prev => ({ ...prev, ...updatedMachine }));
      } else {
        setSelectedMachine(prev => ({ ...prev, deactivateType: newValue }));
      }
    } catch (error) {
      setSelectedMachine(prev => ({ ...prev, deactivateType: previousValue }));
      console.error('Failed to update deactivate type:', error);
    }
  };

  const searchStyleOptions = ['LEGACY', 'HYBRID', 'NEW', 'NEW_STYLE'];

  // DJB-135: control changes stage into featureDraft and only reach the
  // backend when the user hits Save, so everything stays reversible
  const setFeatureDraftValue = (field, value) => {
    setFeatureDraft(prev => ({ ...prev, [field]: value }));
  };

  const draftedFeatureValue = (field, fallback) =>
    featureDraft[field] !== undefined ? featureDraft[field] : fallback;

  const featureDraftDirty = Object.keys(featureDraft).length > 0;

  const handleRevertFeatureDefaults = () => {
    const defaults = {
      giftCreditAfter: 0,          // credit gift = never
      freePlay: false,             // free play = off
      skipQueueCredits: 1,         // skip queue = 1 credit
      creditsPerToken: 3,          // credits per token = 3c / token
      quickCheckout: '50-40-40',   // mobile pay checkout = 50-40-40
      shuffleTracksAmount: 8,      // party shuffle = 8
      searchStyle: 'NEW_STYLE',    // search style = new_style
      liveDelete: true,            // live deletes = on
    };
    // daily bonus / long track price mode are backend-configured lists, so
    // their defaults resolve by name
    const defaultBonus = (bonusConfiguration || []).find(
      b => (b.name || '').toLowerCase() === 'default'
    ) || (bonusConfiguration || []).find(
      b => (b.name || '').toLowerCase().includes('default')
    );
    if (defaultBonus) defaults.bonusId = defaultBonus.id;
    const defaultPriceMode = (longTrackPriceModes || []).find(
      m => (m.name || '') === '400-200-200'
    ) || (longTrackPriceModes || []).find(
      m => (m.name || '').includes('400-200-200')
    );
    if (defaultPriceMode) defaults.longTrackPriceMode = defaultPriceMode.id;
    setFeatureDraft(defaults);
  };

  const handleSaveFeatures = async () => {
    if (!selectedMachine || !featureDraftDirty) return;
    setSavingFeatures(true);
    try {
      // bonus + price mode persist through their own endpoints; the rest goes
      // through the bundled optional-features update
      const { bonusId, longTrackPriceMode, ...featureFields } = featureDraft;
      if (Object.keys(featureFields).length > 0) {
        await updateOptionalFeatures(selectedMachine.id, {
          ...(selectedMachineOptionalFeatures || {}),
          ...featureFields,
          machineId: selectedMachine.id,
        });
      }
      if (bonusId !== undefined && bonusId !== '' &&
          bonusId !== selectedMachineOptionalFeatures?.bonusId) {
        await updateBonusConfiguration(selectedMachine.id, bonusId);
      }
      if (longTrackPriceMode !== undefined && longTrackPriceMode !== '' &&
          longTrackPriceMode !== selectedMachineOptionalFeatures?.longTrackPriceMode) {
        await updateLongTrackPriceMode(selectedMachine.id, longTrackPriceMode);
      }
      setFeatureDraft({});
    } catch (error) {
      console.error('Failed to save optional features:', error);
    } finally {
      setSavingFeatures(false);
    }
  };

  // DJB-87: handler for the hidden screensaver-quiz option; restore together
  // with its field in the optional-features block
  /*
  const handleScreensaverQuizCreditsChange = async (e) => {
    if (!selectedMachine) return;

    const newValue = parseInt(e.target.value);
    const currentFeatures = selectedMachineOptionalFeatures || {};

    const updatedFeatures = {
      ...currentFeatures,
      machineId: selectedMachine.id,
      screensaverQuizCredits: newValue
    };

    try {
      await updateOptionalFeatures(selectedMachine.id, updatedFeatures);
    } catch (error) {
      console.error('Failed to update optional features:', error);
    }
  };
  */

  // DJB-87: handler for the hidden mobile-happy-hour option; restore together
  // with its field in the optional-features block
  /*
  const handleMobileHappyHourChange = async (e) => {
    if (!selectedMachine) return;

    const newValue = e.target.value;
    const currentFeatures = selectedMachineOptionalFeatures || {};

    const updatedFeatures = {
      ...currentFeatures,
      machineId: selectedMachine.id,
      mobileHappyHour: newValue
    };

    try {
      await updateOptionalFeatures(selectedMachine.id, updatedFeatures);
    } catch (error) {
      console.error('Failed to update optional features:', error);
    }
  };
  */

  const handleDownloadHistory = async () => {
    if (!selectedMachine) return;

    try {
      const creditRequests = await fetchCreditRequests(selectedMachine.id, parseInt(historyDays));
      
      // Format current date for filename
      const now = new Date();
      const dateString = now.getFullYear().toString() +
        (now.getMonth() + 1).toString().padStart(2, '0') +
        now.getDate().toString().padStart(2, '0') +
        now.getHours().toString().padStart(2, '0');

      // Create filename
      const fileName = `MACHINE-${selectedMachine.name}-${selectedMachine.city}-${dateString}.csv`
        .replace(/[/\\?%*:|"<>]/g, '-'); // Replace invalid filename characters with dash

      // Find track details from trackId
      const findTrackDetails = (trackId) => {
        for (const artist of artists) {
          for (const album of artist.albums) {
            const track = album.tracks.find(t => t.id === trackId);
            if (track) {
              return {
                title: track.title,
                artistName: artist.name
              };
            }
          }
        }
        return {
          title: 'Unknown Track',
          artistName: 'Unknown Artist'
        };
      };

      // Format date from the requestedAt array
      const formatDate = (dateArray) => {
        const [year, month, day, hour, minute] = dateArray;
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      };

      // Create CSV headers - removed Genre
      const headers = ['Title', 'Artist Name', 'Date of Purchase'];
      
      // Create CSV data - removed genre from the data mapping
      const csvData = creditRequests.map(request => {
        const trackDetails = findTrackDetails(request.trackId);
        return [
          trackDetails.title,
          trackDetails.artistName,
          formatDate(request.requestedAt)
        ];
      });

      // Combine headers and data, properly escape fields
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => 
          row.map(field => 
            `"${String(field).replace(/"/g, '""')}"` // Escape quotes in fields
          ).join(',')
        )
      ].join('\n');

      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('Failed to fetch or process credit requests:', error);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('nl-BE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const calculateDaysBetween = (start, end) => {
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const formatCurrency = (amount) => {
    return (amount / 100).toFixed(2).replace('.', ',') + ' €';
  };

  const exportFinancialHistory = (history, machineName) => {
    const headers = ['Start', 'End', 'Days', 'Cash', 'Mobile Pay', 'Total Income', 'Daily Average'];
    
    const csvData = history.map(entry => {
        const days = calculateDaysBetween(entry.previousCashoutAt, entry.cashoutAt);
        const total = entry.incomeFromCoin + entry.incomeFromPayconiq;
        const dailyAvg = days > 0 ? total / days : 0;
        
        // Wrap each field in quotes and escape any existing quotes
        return [
            `"${formatDate(entry.previousCashoutAt)}"`,
            `"${formatDate(entry.cashoutAt)}"`,
            `"${days}"`,
            `"${formatCurrency(entry.incomeFromCoin)}"`,
            `"${formatCurrency(entry.incomeFromPayconiq)}"`,
            `"${formatCurrency(total)}"`,
            `"${formatCurrency(dailyAvg)}"`
        ];
    });

    const csvContent = [
        headers.map(header => `"${header}"`).join(','),  // Wrap headers in quotes too
        ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `financial_history_${machineName}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Standalone control in view mode (DJB-254 hides it while editing); changing
  // it applies immediately via updateDeactivateType, outside the save flow
  const deactivationFlagMenu = (
    <div className="deactivate-flag-field">
      <label htmlFor="machine-deactivate-type">Deactivation flag:</label>
      <select
        id="machine-deactivate-type"
        value={selectedMachine?.deactivateType ?? 'NONE'}
        onChange={handleDeactivateTypeChange}
      >
        {Object.entries(DEACTIVATE_TYPE_LABELS).map(([value, label]) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="inline-account-details">
      {selectedMachine ? (
        <>
          {/* DJB-155: "Name – Place" bar between the accounts table and the
              detail blocks; sticky, so the selected account stays visible
              while scrolling through the blocks */}
          <div className="selected-account-header">
            <h2 className="selected-account-title">
              {selectedMachine.name} – {selectedMachine.city}
            </h2>
          </div>
          <div className="details-grid">
            <div className="details-block info-block">
              <div className="details-title-row">
                <h2 className="details-title">{selectedMachine.name}</h2>
                {/* DJB-250: in edit mode Save/Cancel take the Edit button's
                    spot in the top-right corner */}
                {isEditingInfo ? (
                  <div className="info-edit-actions">
                    <button
                      className="info-save-btn"
                      onClick={handleSaveInfo}
                      disabled={savingInfo}
                    >
                      {savingInfo ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className="info-cancel-btn"
                      onClick={handleCancelEditInfo}
                      disabled={savingInfo}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button className="info-edit-btn" onClick={handleStartEditInfo}>
                    Edit
                  </button>
                )}
              </div>
              <div className="details-content">
                {isEditingInfo ? (
                  <>
                    {/* DJB-254: edit order Name, Street, Nr, Postal, City,
                        Province, Type, Plan, Openingsdagen; account state,
                        deactivation flag, ID, Last Seen and Software stay
                        hidden while editing */}
                    <div className="info-edit-field">
                      <label htmlFor="machine-name">Name:</label>
                      <input
                        id="machine-name"
                        type="text"
                        value={infoForm.name}
                        onChange={(e) => handleInfoFieldChange('name', e.target.value)}
                      />
                    </div>
                    <div className="info-edit-field">
                      <label htmlFor="machine-street">Street:</label>
                      <input
                        id="machine-street"
                        type="text"
                        value={infoForm.street}
                        onChange={(e) => handleInfoFieldChange('street', e.target.value)}
                      />
                    </div>
                    <div className="info-edit-field">
                      <label htmlFor="machine-housenumber">Nr:</label>
                      <input
                        id="machine-housenumber"
                        type="number"
                        value={infoForm.housenumber}
                        onChange={(e) => handleInfoFieldChange('housenumber', e.target.value)}
                      />
                    </div>
                    <div className="info-edit-field">
                      <label htmlFor="machine-postalcode">Postal:</label>
                      <input
                        id="machine-postalcode"
                        type="number"
                        value={infoForm.postalcode}
                        onChange={(e) => handleInfoFieldChange('postalcode', e.target.value)}
                      />
                    </div>
                    <div className="info-edit-field">
                      <label htmlFor="machine-city">City:</label>
                      <input
                        id="machine-city"
                        type="text"
                        value={infoForm.city}
                        onChange={(e) => handleInfoFieldChange('city', e.target.value)}
                      />
                    </div>
                    <div className="info-edit-field">
                      <label htmlFor="machine-province">Province:</label>
                      <select
                        id="machine-province"
                        value={infoForm.province}
                        onChange={(e) => handleInfoFieldChange('province', e.target.value)}
                      >
                        {provinces.map(province => (
                          <option key={province} value={province}>{province}</option>
                        ))}
                      </select>
                    </div>
                    <div className="info-edit-field">
                      <label htmlFor="machine-type">Type:</label>
                      <select
                        id="machine-type"
                        value={infoForm.type}
                        onChange={(e) => handleInfoFieldChange('type', e.target.value)}
                      >
                        <option value="">—</option>
                        {machineTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div className="info-edit-field">
                      {/* DJB-183: label renamed to Plan; the overeenkomst
                          field/values stay */}
                      <label htmlFor="machine-overeenkomst">Plan:</label>
                      <select
                        id="machine-overeenkomst"
                        value={infoForm.overeenkomst}
                        onChange={(e) => handleInfoFieldChange('overeenkomst', e.target.value)}
                        disabled={isDeactivateFlagActive}
                      >
                        {Object.entries(OVEREENKOMST_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="info-edit-field">
                      <span>Openingsdagen:</span>
                      <div className="open-days-edit">
                        <OpenDaysDots openDays={infoForm.openDays} onToggle={handleToggleOpenDay} />
                        <button
                          type="button"
                          className="open-days-reset-btn"
                          onClick={handleResetOpenDays}
                          disabled={infoForm.openDays === null}
                        >
                          Reset openingsdagen
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* DJB-132: fields grouped into Location / Account /
                        Machine sections with aligned label-value rows */}
                    <div className="info-section">
                      <p className="info-section-label">Location</p>
                      {/* DJB-250: Maps link right-aligned next to the address */}
                      <div className="info-address-row">
                        <div className="info-address">
                          <p>{selectedMachine.street} {selectedMachine.housenumber}</p>
                          <p>{selectedMachine.postalcode} {selectedMachine.city}</p>
                          <p>{selectedMachine.province}</p>
                        </div>
                        <a
                          href={getGoogleMapsUrl(selectedMachine)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="maps-link"
                        >
                          Show in Maps
                        </a>
                      </div>
                    </div>
                    <div className="details-spacer"></div>
                    <div className="info-section">
                      <p className="info-section-label">Openingsdagen</p>
                      <OpenDaysDots openDays={selectedMachine.openDays} todayIndex={getBrusselsTodayIndex()} />
                    </div>
                    <div className="details-spacer"></div>
                    <div className="info-section">
                      <p className="info-section-label">Account</p>
                      <div className="info-row">
                        <span className="info-label">Type</span>
                        <span className="info-value">{selectedMachine.type}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Plan</span>
                        <span className="info-value">{OVEREENKOMST_LABELS[selectedMachine.overeenkomst] || 'Unassigned'}</span>
                      </div>
                      {selectedMachine.accountState && (
                        <div className="info-row">
                          <span className="info-label">Account state</span>
                          <span className="info-value">
                            <span className={`account-state-badge ${(selectedMachine.accountState || '').toLowerCase()}`}>
                              {ACCOUNT_STATE_LABELS[selectedMachine.accountState] || (selectedMachine.accountState || '').toLowerCase()}
                            </span>
                          </span>
                        </div>
                      )}
                      <div className="info-row">
                        <span className="info-label">ID</span>
                        <span className="info-value info-value-id">{selectedMachine.id}</span>
                      </div>
                    </div>
                    <div className="details-spacer"></div>
                    <div className="info-section">
                      <p className="info-section-label">Machine</p>
                      <div className="info-row">
                        <span className="info-label">Last Seen</span>
                        <span className="info-value">{formatLastSeen(selectedMachine.lastSeen)}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Software</span>
                        <span className="info-value">{selectedMachine.softwareVersion || 'Unknown'}</span>
                      </div>
                      {deactivationFlagMenu}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="details-block stats-block">
              <h2 className="details-title">Current Stats</h2>
              <div className="details-content">
                <div className="stats-section">
                  <p className="stats-section-label">Income</p>
                  <div className="stat-group">
                    <div className="stat-row">
                      <span className="stat-label">Cash</span>
                      <span className="detail-stat-value">{((selectedMachine.currentCoinDeposit || 0) / 100).toFixed(2).replace('.', ',')} €</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">Mobile Pay</span>
                      <span className="detail-stat-value">{((selectedMachine.currentIncomeFromPayconiq || 0) / 100).toFixed(2).replace('.', ',')} €</span>
                    </div>
                    <div className="stat-row stat-row-total">
                      <span className="stat-label">Total Income</span>
                      <span className="detail-stat-value">{calculateRevenue(selectedMachine)}</span>
                    </div>
                  </div>
                  <div className="stat-group">
                    <div className="stat-row">
                      <span className="stat-label">Income Days</span>
                      <span className="detail-stat-value">{calculateDaysSinceClear(selectedMachine.coinsLastEmptied)}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">AVG Income/day</span>
                      <span className="detail-stat-value">{calculateAverageIncome(selectedMachine)}</span>
                    </div>
                  </div>
                </div>
                <div className="details-spacer"></div>
                <div className="stats-section">
                  <p className="stats-section-label">Usage</p>
                  <div className="stat-group">
                    <div className="stat-row">
                      <span className="stat-label">Current Credits</span>
                      <span className="detail-stat-value">{selectedMachine.currentCredits || 0}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">Songs Requested</span>
                      <span className="detail-stat-value">{selectedMachine.songsRequestedSince || 0}</span>
                    </div>
                  </div>
                </div>
                {hasPaidOvereenkomst && tokenBalance !== null && (
                  <>
                    <div className="details-spacer"></div>
                    <div className="stats-section">
                      <p className="stats-section-label">Tokens</p>
                      <div className="stat-group">
                        <div className="stat-row">
                          <span className="stat-label">Tokens left</span>
                          <span className="detail-stat-value">{tokenBalance}</span>
                        </div>
                      </div>
                    </div>
                    <p className="token-info-text">
                      tokens will be automatically reset in {getDaysUntilNextMonth()} days ({selectedMachine.overeenkomst}{getDefaultTokensForOvereenkomst() !== null ? ` - ${getDefaultTokensForOvereenkomst()} tokens` : ''})
                    </p>
                    <div className="token-actions">
                      <button className="token-btn token-reset-btn" onClick={handleResetTokens}>
                        Reset now
                      </button>
                      <button className="token-btn token-add-btn" onClick={handleAddTokens}>
                        +10
                      </button>
                    </div>
                  </>
                )}
                <div className="details-spacer"></div>
                <button
                  className="clear-period-btn"
                  onClick={() => setShowClearPeriodDialog(true)}
                >
                  Clear period
                </button>
              </div>
              {showClearPeriodDialog && (
                <div className="clear-period-overlay">
                  <div className="clear-period-dialog">
                    <h3 className="clear-period-title">Boekhoudperiode afsluiten?</h3>
                    <p className="clear-period-text">
                      Je sluit de huidige boekhoudperiode af en start een nieuwe voor deze machine.
                      De huidige stats (cash, mobiele betalingen, aangevraagde songs en income-dagen)
                      worden op nul gezet. Beschikbare credits blijven behouden.
                    </p>
                    <p className="clear-period-warning">
                      ⚠️ Let op: offline machines kunnen nog niet-gesynchroniseerde betalingen
                      bevatten. Die worden pas verwerkt zodra de jukebox opnieuw online komt,
                      waardoor de werkelijke inhoud van de geldlade kan afwijken van de hier
                      getoonde bedragen.
                    </p>
                    <div className="clear-period-actions">
                      <button
                        className="info-cancel-btn"
                        onClick={() => setShowClearPeriodDialog(false)}
                        disabled={clearingPeriod}
                      >
                        Annuleren
                      </button>
                      <button
                        className="clear-period-confirm-btn"
                        onClick={handleConfirmClearPeriod}
                        disabled={clearingPeriod}
                      >
                        {clearingPeriod ? 'Bezig met afsluiten...' : 'Afsluiten en nieuwe periode starten'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="details-block financial-block">
              <div className="financial-history-header">
                <h2 className="details-title">Financial History</h2>
                <button
                  className="export-button"
                  onClick={() => exportFinancialHistory(selectedMachine.financialHistory, selectedMachine.name)}
                >
                  Export to CSV
                </button>
              </div>
              <div className="financial-table-container">
                <table className="financial-table">
                  <thead>
                    {/* DJB-185: Start/End replaced by the single Cleared date
                        (when the period was cleared); Total/Avg shortened */}
                    <tr>
                      <th>Cleared</th>
                      <th>Days</th>
                      <th>Cash</th>
                      <th>Mobile Pay</th>
                      <th>Total</th>
                      <th>Avg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...selectedMachine.financialHistory]
                      .sort((a, b) => {
                        // Compare start dates (previousCashoutAt)
                        const aStart = new Date(a.previousCashoutAt);
                        const bStart = new Date(b.previousCashoutAt);
                        if (bStart - aStart !== 0) return bStart - aStart;
                        // If start dates are equal, compare end dates (cashoutAt)
                        const aEnd = new Date(a.cashoutAt);
                        const bEnd = new Date(b.cashoutAt);
                        return bEnd - aEnd;
                      })
                      .map((entry, index) => {
                        const days = calculateDaysBetween(entry.previousCashoutAt, entry.cashoutAt);
                        const total = entry.incomeFromCoin + entry.incomeFromPayconiq;
                        const dailyAvg = days > 0 ? total / days : 0;
                        return (
                          <tr key={index}>
                            <td>{formatDate(entry.cashoutAt)}</td>
                            <td>{days}</td>
                            <td>{formatCurrency(entry.incomeFromCoin)}</td>
                            <td>{formatCurrency(entry.incomeFromPayconiq)}</td>
                            <td>{formatCurrency(total)}</td>
                            <td>{formatCurrency(dailyAvg)}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="details-block playlists-block">
              <h2 className="details-title">Playlists</h2>
              <div className="details-content">
                {/* DJB-133: one searchbar filters both tables below */}
                <div className="playlist-search">
                  <input
                    type="text"
                    placeholder="Search Playlists"
                    value={playlistSearchTerm}
                    onChange={handlePlaylistSearchChange}
                    className="details-search-box"
                  />
                  {/* DJB-255: clears the whole search term */}
                  {playlistSearchTerm !== '' && (
                    <button
                      type="button"
                      className="search-clear-btn"
                      aria-label="Clear search"
                      title="Clear search"
                      onClick={() => setPlaylistSearchTerm('')}
                    >
                      ×
                    </button>
                  )}
                </div>
                <div className={`playlist-table-section ${globalsTableOpen ? 'is-open' : ''}`}>
                  <div className="playlist-table-caption">
                    {/* DJB-138/157: chevron header expands the table and
                        collapses the other one */}
                    <button
                      type="button"
                      className="playlist-collapse-toggle"
                      aria-expanded={globalsTableOpen}
                      aria-controls="playlist-globals-list"
                      onClick={() => togglePlaylistTable('globals')}
                    >
                      <span
                        className={`playlist-collapse-chevron ${globalsTableOpen ? '' : 'is-collapsed'}`}
                        aria-hidden="true"
                      />
                      <span className="playlist-table-title">Custom Globals</span>
                    </button>
                    <span className="playlist-count">
                      {customGlobalPlaylists.filter(p => selectedMachine?.playlists?.includes(p.id)).length}
                      /{customGlobalPlaylists.length} Subscribed
                    </span>
                  </div>
                  {globalsTableOpen && (
                  <div className="playlist-list playlist-globals-list" id="playlist-globals-list">
                    <table className="playlist-table playlist-globals-table">
                      <tbody>
                        {globalPlaylistCategories.map(category => {
                          const visiblePlaylists = category.playlists.filter(matchesPlaylistSearch);
                          if (visiblePlaylists.length === 0) return null;
                          const allSubscribed = category.playlists.every(
                            p => selectedMachine?.playlists?.includes(p.id)
                          );
                          return (
                            <React.Fragment key={category.id}>
                              <tr className="playlist-category-row">
                                <td colSpan={3}>
                                  <div className="playlist-category-head">
                                    <span className="playlist-category-name">{category.name}</span>
                                    <button
                                      className="subscribe-category-btn"
                                      onClick={() => handleSubscribeCategory(category)}
                                      disabled={allSubscribed || subscribingCategoryId !== null}
                                    >
                                      {subscribingCategoryId === category.id
                                        ? 'Subscribing...'
                                        : allSubscribed ? 'All subscribed' : 'Subscribe all'}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              {visiblePlaylists.map(playlist => (
                                <tr key={playlist.id} className="playlist-row">
                                  <td className="playlist-check-cell">
                                    <input
                                      type="checkbox"
                                      checked={selectedMachine?.playlists?.includes(playlist.id)}
                                      onChange={() => handlePlaylistToggle(playlist.id)}
                                      id={`playlist-${playlist.id}`}
                                    />
                                  </td>
                                  <td className="playlist-name-cell">
                                    <label htmlFor={`playlist-${playlist.id}`}>
                                      {getPlaylistDisplayName(playlist)}
                                    </label>
                                  </td>
                                  <td className="playlist-star-cell">
                                    {renderPriorityStar(playlist)}
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  )}
                </div>
                <div className={`playlist-table-section ${localsTableOpen ? 'is-open' : ''}`}>
                  <div className="playlist-table-caption">
                    <button
                      type="button"
                      className="playlist-collapse-toggle"
                      aria-expanded={localsTableOpen}
                      aria-controls="playlist-locals-list"
                      onClick={() => togglePlaylistTable('locals')}
                    >
                      <span
                        className={`playlist-collapse-chevron ${localsTableOpen ? '' : 'is-collapsed'}`}
                        aria-hidden="true"
                      />
                      <span className="playlist-table-title">Custom Locals</span>
                    </button>
                    <span className="playlist-count">
                      {customLocalPlaylists.filter(p => selectedMachine?.playlists?.includes(p.id)).length}
                      /{customLocalPlaylists.length} Subscribed
                    </span>
                  </div>
                  {localsTableOpen && (
                  <div className="playlist-list playlist-locals-list" id="playlist-locals-list">
                    <table className="playlist-table playlist-locals-table">
                      <tbody>
                        {customLocalPlaylists
                          .filter(matchesPlaylistSearch)
                          .sort((a, b) => {
                            const aEnabled = selectedMachine?.playlists?.includes(a.id) ? 1 : 0;
                            const bEnabled = selectedMachine?.playlists?.includes(b.id) ? 1 : 0;
                            if (aEnabled !== bEnabled) return bEnabled - aEnabled; // subscribed playlists first
                            return getPlaylistDisplayName(a).localeCompare(getPlaylistDisplayName(b));
                          })
                          .map(playlist => (
                            <tr key={playlist.id} className="playlist-row">
                              <td className="playlist-check-cell">
                                <input
                                  type="checkbox"
                                  checked={selectedMachine?.playlists?.includes(playlist.id)}
                                  onChange={() => handlePlaylistToggle(playlist.id)}
                                  id={`playlist-${playlist.id}`}
                                />
                              </td>
                              <td className="playlist-name-cell">
                                <label htmlFor={`playlist-${playlist.id}`}>
                                  {getPlaylistDisplayName(playlist)} ({playlist.playlistType})
                                </label>
                              </td>
                              <td className="playlist-star-cell">
                                {renderPriorityStar(playlist)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  )}
                </div>
              </div>
            </div>
            <div className="details-block optional-features-block">
              <div className="details-title-row">
                <h2 className="details-title">Optional Features</h2>
                {/* DJB-135: populates the controls below with the defaults;
                    nothing persists until Save */}
                <button
                  className="revert-defaults-btn"
                  onClick={handleRevertFeatureDefaults}
                  disabled={!selectedMachineOptionalFeatures}
                >
                  Revert to default
                </button>
              </div>
              <div className="details-content">
                {selectedMachine && optionalFeatures && selectedMachineOptionalFeatures ? (
                  <>
                    <div className="optional-feature-field">
                      <label htmlFor="gift-credit-dropdown" className="optional-feature-label">
                        CREDIT GIFT:
                      </label>
                      <select
                        id="gift-credit-dropdown"
                        value={draftedFeatureValue('giftCreditAfter', selectedMachineOptionalFeatures.giftCreditAfter ?? 0)}
                        onChange={(e) => setFeatureDraftValue('giftCreditAfter', parseInt(e.target.value))}
                        className="optional-feature-dropdown"
                      >
                        <option value={0}>NEVER</option>
                        <option value={30}>30 minutes</option>
                        <option value={60}>60 minutes</option>
                        <option value={90}>90 minutes</option>
                        <option value={120}>120 minutes</option>
                      </select>
                    </div>
                    <div className="optional-feature-field">
                      <label htmlFor="free-play-dropdown" className="optional-feature-label">
                        FREE PLAY:
                      </label>
                      <select
                        id="free-play-dropdown"
                        value={String(draftedFeatureValue('freePlay', selectedMachineOptionalFeatures.freePlay ?? false))}
                        onChange={(e) => setFeatureDraftValue('freePlay', e.target.value === 'true')}
                        className="optional-feature-dropdown"
                      >
                        <option value="false">OFF</option>
                        <option value="true">ON</option>
                      </select>
                    </div>
                    <div className="optional-feature-field">
                      <label htmlFor="skip-queue-dropdown" className="optional-feature-label">
                        Skip queue:
                      </label>
                      <select
                        id="skip-queue-dropdown"
                        value={draftedFeatureValue('skipQueueCredits', selectedMachineOptionalFeatures.skipQueueCredits ?? -1)}
                        onChange={(e) => setFeatureDraftValue('skipQueueCredits', parseInt(e.target.value))}
                        className="optional-feature-dropdown"
                      >
                        <option value={-1}>OFF</option>
                        <option value={0}>FREE</option>
                        <option value={1}>1 Credit</option>
                        <option value={2}>2 Credits</option>
                        <option value={3}>3 Credits</option>
                        <option value={4}>5 Credits</option>
                      </select>
                    </div>
                    <div className="optional-feature-field">
                      <label htmlFor="credits-per-token-dropdown" className="optional-feature-label">
                        Credits per token:
                      </label>
                      <select
                        id="credits-per-token-dropdown"
                        value={draftedFeatureValue('creditsPerToken', selectedMachineOptionalFeatures.creditsPerToken ?? 3)}
                        onChange={(e) => setFeatureDraftValue('creditsPerToken', parseInt(e.target.value))}
                        className="optional-feature-dropdown"
                      >
                        <option value={1}>1c / Token</option>
                        <option value={2}>2c / Token</option>
                        <option value={3}>3c / Token</option>
                        <option value={5}>5c / Token</option>
                        <option value={10}>10c / Token</option>
                      </select>
                    </div>
                    <div className="optional-feature-field">
                      <label htmlFor="quick-checkout-dropdown" className="optional-feature-label">
                        Mobile Pay checkout:
                      </label>
                      <select
                        id="quick-checkout-dropdown"
                        value={draftedFeatureValue('quickCheckout', selectedMachineOptionalFeatures.quickCheckout ?? 'OFF')}
                        onChange={(e) => setFeatureDraftValue('quickCheckout', e.target.value)}
                        className="optional-feature-dropdown"
                      >
                        <option value="OFF">OFF</option>
                        <option value="50-40-40">50-40-40</option>
                      </select>
                    </div>
                    <div className="optional-feature-field">
                      <label htmlFor="daily-bonus-dropdown" className="optional-feature-label">
                        Daily Bonus:
                      </label>
                      <select
                        id="daily-bonus-dropdown"
                        value={draftedFeatureValue('bonusId', selectedMachineOptionalFeatures.bonusId ?? '')}
                        onChange={(e) => setFeatureDraftValue('bonusId', e.target.value)}
                        className="optional-feature-dropdown"
                      >
                        {bonusConfiguration && bonusConfiguration.map((bonus) => (
                          <option key={bonus.id} value={bonus.id}>
                            {bonus.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="optional-feature-field">
                      <label htmlFor="long-track-price-mode-dropdown" className="optional-feature-label">
                        Long Track Price Mode:
                      </label>
                      <select
                        id="long-track-price-mode-dropdown"
                        value={draftedFeatureValue('longTrackPriceMode', selectedMachineOptionalFeatures.longTrackPriceMode ?? '')}
                        onChange={(e) => setFeatureDraftValue('longTrackPriceMode', e.target.value)}
                        className="optional-feature-dropdown"
                      >
                        {longTrackPriceModes && longTrackPriceModes.map((mode) => (
                          <option key={mode.id} value={mode.id}>
                            {mode.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* DJB-87: screensaver-quiz is not in use yet; restore this
                        field once the feature ships
                    <div className="optional-feature-field">
                      <label htmlFor="screensaver-quiz-dropdown" className="optional-feature-label">
                        screensaverQuizCredits:
                      </label>
                      <select
                        id="screensaver-quiz-dropdown"
                        value={selectedMachineOptionalFeatures.screensaverQuizCredits ?? -1}
                        onChange={handleScreensaverQuizCreditsChange}
                        className="optional-feature-dropdown"
                        disabled
                      >
                        <option value={-1}>Off</option>
                        <option value={0}>Geen credit winst</option>
                        <option value={1}>credit winst bij goed antwoord</option>
                      </select>
                    </div>
                    */}
                    <div className="optional-feature-field">
                      <label htmlFor="party-shuffle-dropdown" className="optional-feature-label">
                        Party Shuffle:
                      </label>
                      <select
                        id="party-shuffle-dropdown"
                        value={draftedFeatureValue('shuffleTracksAmount', selectedMachineOptionalFeatures.shuffleTracksAmount ?? 0)}
                        onChange={(e) => setFeatureDraftValue('shuffleTracksAmount', parseInt(e.target.value))}
                        className="optional-feature-dropdown"
                      >
                        <option value={0}>OFF</option>
                        <option value={6}>6</option>
                        <option value={8}>8</option>
                        <option value={10}>10</option>
                      </select>
                    </div>
                    {/* DJB-87: mobile-happy-hour is not in use yet; restore this
                        field once the feature ships
                    <div className="optional-feature-field">
                      <label htmlFor="mobile-happy-hour-dropdown" className="optional-feature-label">
                        mobileHappyHour:
                      </label>
                      <select
                        id="mobile-happy-hour-dropdown"
                        value={selectedMachineOptionalFeatures.mobileHappyHour ?? ''}
                        onChange={handleMobileHappyHourChange}
                        className="optional-feature-dropdown"
                        disabled
                      >
                        <option value="">off</option>
                      </select>
                    </div>
                    */}
                    <div className="optional-feature-field">
                      <label htmlFor="search-style-dropdown" className="optional-feature-label">
                        Search style:
                      </label>
                      <select
                        id="search-style-dropdown"
                        value={draftedFeatureValue('searchStyle', selectedMachineOptionalFeatures.searchStyle ?? '')}
                        onChange={(e) => setFeatureDraftValue('searchStyle', e.target.value)}
                        className="optional-feature-dropdown"
                      >
                        <option value="">—</option>
                        {searchStyleOptions.map(style => (
                          <option key={style} value={style}>{style}</option>
                        ))}
                      </select>
                    </div>
                    <div className="optional-feature-field">
                      <label htmlFor="live-delete-dropdown" className="optional-feature-label">
                        Live deletes:
                      </label>
                      <select
                        id="live-delete-dropdown"
                        value={String(draftedFeatureValue('liveDelete', selectedMachineOptionalFeatures.liveDelete ?? false))}
                        onChange={(e) => setFeatureDraftValue('liveDelete', e.target.value === 'true')}
                        className="optional-feature-dropdown"
                      >
                        <option value="false">OFF</option>
                        <option value="true">ON</option>
                      </select>
                    </div>
                    {featureDraftDirty && (
                      <div className="optional-features-actions">
                        <button
                          className="info-save-btn"
                          onClick={handleSaveFeatures}
                          disabled={savingFeatures}
                        >
                          {savingFeatures ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          className="info-cancel-btn"
                          onClick={() => setFeatureDraft({})}
                          disabled={savingFeatures}
                        >
                          Discard
                        </button>
                      </div>
                    )}
                  </>
                ) : selectedMachine && optionalFeatures ? (
                  <p className="optional-features-not-found">Could not find optional features</p>
                ) : null}
              </div>
            </div>
            {/* DJB-136: block renamed Downloads; the music-requests download
                lives in its own section next to the Player Logs placeholder */}
            <div className="details-block music-requests-block">
              <h2 className="details-title">Downloads</h2>
              <div className="details-content">
                <div className="downloads-section">
                  <p className="info-section-label">Music Request</p>
                  <div className="history-input-group">
                    <div className="history-input-container">
                      <span className="history-days-label">Music requests from the last</span>
                      <input
                        type="number"
                        min="1"
                        value={historyDays}
                        onChange={handleDaysChange}
                        className="history-days-input"
                      />
                      <span className="history-days-label">days</span>
                    </div>
                    <button
                      className="download-history-btn"
                      onClick={handleDownloadHistory}
                      disabled={!selectedMachine}
                    >
                      Download History
                    </button>
                  </div>
                </div>
                <div className="details-spacer"></div>
                <div className="downloads-section downloads-placeholder-section">
                  <p className="info-section-label">Player Logs</p>
                  {/* Placeholder: player-log downloads land in a later ticket */}
                  <p className="downloads-placeholder">Player log downloads will appear here soon.</p>
                </div>
              </div>
            </div>
            <div className="details-block new-box-2-block">
              <h2 className="details-title">Token Config</h2>
              <div className="details-content">
                {tokenConfigs === null && !tokenConfigsLoading && !tokenConfigsError && (
                  <button className="token-btn token-reset-btn" onClick={loadTokenConfigs}>
                    Load token configs
                  </button>
                )}
                {tokenConfigsLoading && <p>Loading configs...</p>}
                {tokenConfigsError && (
                  <>
                    <p className="token-info-text">{tokenConfigsError}</p>
                    <button className="token-btn token-reset-btn" onClick={loadTokenConfigs}>
                      Retry
                    </button>
                  </>
                )}
                {tokenConfigs !== null && !tokenConfigsLoading && (
                  <>
                    {Array.isArray(tokenConfigs) && tokenConfigs.length > 0 ? (
                      tokenConfigs.map(config => (
                        <div key={config.overeenkomstType} className="token-config-row">
                          <span className="token-config-label">{config.overeenkomstType}:</span>
                          <input
                            type="number"
                            className="token-config-input"
                            value={editingTokenConfigs[config.overeenkomstType] !== undefined
                              ? editingTokenConfigs[config.overeenkomstType]
                              : config.defaultTokens}
                            onChange={(e) => setEditingTokenConfigs(prev => ({
                              ...prev,
                              [config.overeenkomstType]: e.target.value
                            }))}
                          />
                          {editingTokenConfigs[config.overeenkomstType] !== undefined && (
                            <button
                              className="token-btn token-add-btn"
                              onClick={() => handleSaveTokenConfig(config.overeenkomstType)}
                            >
                              Save
                            </button>
                          )}
                        </div>
                      ))
                    ) : Array.isArray(tokenConfigs) ? (
                      <p className="token-info-text">No token configs found. Backend endpoint may not be available yet.</p>
                    ) : (
                      Object.entries(tokenConfigs).map(([type, amount]) => (
                        <div key={type} className="token-config-row">
                          <span className="token-config-label">{type}:</span>
                          <input
                            type="number"
                            className="token-config-input"
                            value={editingTokenConfigs[type] !== undefined
                              ? editingTokenConfigs[type]
                              : (typeof amount === 'object' ? amount.defaultTokens : amount)}
                            onChange={(e) => setEditingTokenConfigs(prev => ({
                              ...prev,
                              [type]: e.target.value
                            }))}
                          />
                          {editingTokenConfigs[type] !== undefined && (
                            <button
                              className="token-btn token-add-btn"
                              onClick={() => handleSaveTokenConfig(type)}
                            >
                              Save
                            </button>
                          )}
                        </div>
                      ))
                    )}
                    <button className="token-btn token-reset-btn" style={{ marginTop: '10px' }} onClick={loadTokenConfigs}>
                      Refresh
                    </button>
                  </>
                )}
              </div>
            </div>
            <EventLogsBlock machine={selectedMachine} />
          </div>
        </>
      ) : (
        <p className="inline-details-empty">
          Select an account in the table above to view its details
        </p>
      )}
    </div>
  );
};

export default MachineDetails;
